import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';

function ImageCarouselModal({ images, deliveryId, recipientName, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  function nextImage() {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setIsZoomed(false);
  }

  function prevImage() {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setIsZoomed(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'ArrowLeft') prevImage();
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'Escape') onClose();
  }

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors z-10"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      {/* Header Info */}
      <div className="absolute top-4 left-4 z-10">
        <div className="bg-black/50 backdrop-blur-sm rounded-lg px-4 py-2 text-white">
          <p className="text-sm font-semibold font-montserrat">{recipientName}</p>
          <p className="text-xs text-white/80 font-montserrat">ID: {deliveryId}</p>
        </div>
      </div>

      {/* Image Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
        <div className="bg-black/50 backdrop-blur-sm rounded-lg px-4 py-2 text-white">
          <p className="text-sm font-semibold font-montserrat">
            {currentIndex + 1} / {images.length}
          </p>
        </div>
      </div>

      {/* Zoom Toggle */}
      <button
        onClick={() => setIsZoomed(!isZoomed)}
        className="absolute bottom-20 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors z-10 sm:bottom-4"
      >
        {isZoomed ? (
          <ZoomOut className="w-5 h-5 text-white" />
        ) : (
          <ZoomIn className="w-5 h-5 text-white" />
        )}
      </button>

      {/* Main Image Container */}
      <div className="relative w-full h-full flex items-center justify-center">
        <div
          className={`relative transition-all duration-300 ${
            isZoomed ? 'w-full h-full' : 'max-w-4xl max-h-[80vh]'
          }`}
        >
          <img
            src={images[currentIndex]}
            alt={`Delivery ${deliveryId} - Image ${currentIndex + 1}`}
            className={`w-full h-full object-contain rounded-lg ${
              isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
            }`}
            onClick={() => setIsZoomed(!isZoomed)}
          />
        </div>

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 sm:left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors backdrop-blur-sm"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 sm:right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors backdrop-blur-sm"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail Strip (Desktop) */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 hidden sm:flex gap-2 bg-black/50 backdrop-blur-sm rounded-lg p-2 max-w-2xl overflow-x-auto">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setIsZoomed(false);
              }}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                index === currentIndex
                  ? 'border-[#B1336E] scale-110'
                  : 'border-white/30 hover:border-white/60'
              }`}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Mobile Navigation Dots */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 sm:hidden">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setIsZoomed(false);
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-[#B1336E] w-6'
                  : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ImageCarouselModal;